import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import {
  CellClassParams,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGridPage } from '../../components/data-grid';
import { FilterTextField } from '../../components/filters';
import FilterSelectField from '../../components/filters/filter-select-field.tsx';
import { useInteractiveGrid } from '../../hooks';
import { useAppSelector } from '../../store/hooks';
import { Dealer, DealerFilters } from '../../types';
import { formatPhoneNumber, getStatusLabel } from '../../utils';
import {
  selectAccessDealers,
  selectManageDealers,
  selectManageManufacturers,
  selectManageTechnicians,
} from '../user';
import { useGetDealersQuery, usePrefetch } from './dealer-api-slice.ts';

// Create a DealerActionCell component that shows different icons based on user role
const DealerActionCell = (params: ICellRendererParams) => {
  const navigate = useNavigate();
  const canManageTechnicians = useAppSelector(selectManageTechnicians);

  return (
    <Tooltip title={canManageTechnicians ? 'Edit Dealer' : 'View Dealer'}>
      <IconButton
        size="small"
        onClick={() => navigate(`/dealer-profile/${params.data.id}`)}
        aria-label={canManageTechnicians ? 'edit dealer' : 'view dealer'}
      >
        {canManageTechnicians ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const DealerManagementPage = () => {
  const hasAccess = useAppSelector(selectAccessDealers);
  const prefetchDealers = usePrefetch('getDealers');
  const navigate = useNavigate();

  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canManageDealers = useAppSelector(selectManageDealers);

  const {
    gridContainerRef,
    pagination,
    filters,
    filtersExpanded,
    setFiltersExpanded,
    response: dealersResponse,
    error,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    onSortChanged,
    handleClearFilters,
    handleNextPageHover,
  } = useInteractiveGrid<DealerFilters, Dealer>({
    hasAccess,
    prefetchFunction: prefetchDealers,
    getDataQuery: useGetDealersQuery,
    initialFilters: {
      status: 'true',
    },
  });

  // Column definitions for AG Grid
  const columnDefs = useMemo<ColDef[]>(() => {
    const columns: ColDef[] = [];
    // Only add manufacturer column for admin users
    if (canManageManufacturers) {
      columns.push({
        field: 'manufacturer',
        headerName: 'Manufacturer',
        sortable: true,
        flex: 1.5,
      });
    }

    columns.push(
      {
        field: 'name',
        headerName: 'Dealer Name',
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'code',
        headerName: 'Code',
        sortable: true,
        width: 80,
      },
      {
        field: 'city',
        headerName: 'City',
        sortable: true,
        flex: 1,
      },
      {
        field: 'state',
        headerName: 'State',
        sortable: true,
        width: 100,
      },
      {
        field: 'phone',
        headerName: 'Phone',
        sortable: true,
        width: 150,
        valueFormatter: (params: ValueFormatterParams) => formatPhoneNumber(params.data?.phone),
      },
      {
        field: 'email',
        headerName: 'Email',
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'activeStatus',
        headerName: 'Status',
        sortable: true,
        width: 85,
        valueFormatter: (params: ValueFormatterParams) => getStatusLabel(params.data?.status),
        cellStyle: (params: CellClassParams<Dealer, string>) => {
          return params.data?.status ? { color: 'green' } : { color: 'red' };
        },
      },
      {
        headerName: canManageDealers ? 'Actions' : 'View',
        width: 80,
        sortable: false,
        cellRenderer: DealerActionCell,
      }
    );

    return columns;
  }, [canManageManufacturers, canManageDealers]);

  const statusOptions = useMemo(
    () => [
      { value: 'true', label: getStatusLabel(true) },
      { value: 'false', label: getStatusLabel(false) },
    ],
    []
  );

  if (!hasAccess) {
    return null;
  }

  const renderFilterControls = () => {
    return (
      <Grid container spacing={1}>
        {/* Only show manufacturer filter for admin users */}
        {canManageManufacturers && (
          <Grid item xs={12} sm={6} md={1.5}>
            <FilterTextField
              label="Manufacturer"
              value={filters.manufacturer ?? ''}
              onChange={value => handleFilterChange('manufacturer', value)}
              hasValue={!!filters.manufacturer}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={2}>
          <FilterTextField
            label="Dealer Name"
            value={filters.name ?? ''}
            onChange={value => handleFilterChange('name', value)}
            hasValue={!!filters.name}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="Code"
            value={filters.code ?? ''}
            onChange={value => handleFilterChange('code', value)}
            hasValue={!!filters.code}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="City"
            value={filters.city ?? ''}
            onChange={value => handleFilterChange('city', value)}
            hasValue={!!filters.city}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="State"
            value={filters.state ?? ''}
            onChange={value => handleFilterChange('state', value)}
            hasValue={!!filters.state}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="Phone"
            value={filters.phone ?? ''}
            onChange={value => handleFilterChange('phone', value)}
            hasValue={!!filters.phone}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FilterTextField
            label="Email"
            value={filters.email ?? ''}
            onChange={value => handleFilterChange('email', value)}
            hasValue={!!filters.email}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterSelectField
            label="Status"
            value={filters.status ?? ''}
            onChange={value => handleFilterChange('status', value === '' ? undefined : value)}
            hasValue={filters.status !== undefined && filters.status !== ''}
            options={statusOptions}
            allOptionLabel="All Status"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            size="small"
            sx={{ fontSize: '0.75rem' }}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <DataGridPage<Dealer, DealerFilters>
      title="Dealer Management"
      addButtonText="Add Dealer"
      addButtonPath="/dealer-profile"
      columnDefs={columnDefs}
      response={dealersResponse}
      error={error}
      pagination={pagination}
      filters={filters}
      filtersExpanded={filtersExpanded}
      gridContainerRef={gridContainerRef}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSortChanged={onSortChanged}
      handleNextPageHover={handleNextPageHover}
      setFiltersExpanded={setFiltersExpanded}
      renderFilterControls={renderFilterControls}
      navigate={navigate}
      hideAddButton={!canManageDealers}
    />
  );
};

export default DealerManagementPage;
